// Edge-aware placement for the CSS-only gl-tooltip primitive.
//
// `[data-gl-tooltip]` renders its bubble with pure CSS at an AUTHORED placement
// (above by default, `data-gl-tooltip-pos` otherwise) centered on the host.
// CSS cannot know where the host sits, and near an edge that placement fails
// in two ways:
//
//   - horizontally: a centered bubble leaves a clipping container or the
//     viewport — the DetailPanel's PAYMENT / DATE command buttons sit near
//     the panel's `overflow: hidden` left edge and are cut off mid-word;
//   - vertically: an above-placed bubble on a host at the TOP of a clipping
//     container has nowhere to go — the datatable toolbar's filter-chip
//     removes and the GitHub-sync warning sit directly under the fixed
//     navbar inside the `overflow: hidden` full-page wrapper, so their bubbles
//     clip at the wrapper's top edge and paint under the navbar; the fixed
//     sidebar toggle loses its bubble above the viewport the same way.
//
// This installs ONE document-level listener (mouseover + focusin, the two ways
// the bubble appears). On each show it measures the bubble with an offscreen
// probe carrying the bubble's typography, intersects the nearest
// overflow-clipping ancestors with the viewport, and stamps two attributes
// _tooltip.scss keys placements off:
//
//   data-gl-tooltip-flip="top|bottom|left|right"  — the OPPOSITE placement,
//       when the authored one does not fit and the opposite has more room;
//   data-gl-tooltip-align="start|end"             — hug the host's start/end
//       edge instead of centering, for above/below placements only.
//
// Both are removed when the authored placement fits, so a host that moves
// (panel resize, scroll) is re-judged on its next show. Zero per-host setup,
// so it covers HAML, Stimulus and React hosts alike.
//
// PORTAL — when no placement can fit (#11981). A host inside a clipping
// ancestor shorter than the bubble has nowhere to put a CSS bubble at all:
// the right-panel command strip is a 50px-tall `overflow-x: auto` scroller
// (`overflow-x: auto` forces vertical clipping per the CSS overflow spec, the
// #10138 class), so every command-tile tooltip is clipped in every direction.
// The same shape: the mobile toolbar action strip (#11984), the 15px snapshot
// value cell around Copy Phone / Copy Email (#11982). Flip and align cannot
// help — both sides have no room. For exactly those hosts (neither the
// authored side nor its opposite fits inside the clip bounds, or the bubble is
// wider than the bounds) the runtime shows ONE shared body-level bubble
// (`[data-gl-tooltip-bubble]`, `position: fixed`, so no ancestor clips it),
// placed against the viewport with the same flip/align rules, and stamps
// `data-gl-tooltip-portal` on the host so its own pseudo-bubble stays hidden.
// It closes on mouseout/focusout, on Escape and whenever another host shows;
// before Turbo Drive caches a page it is closed AND detached, with the
// measuring probe, so the snapshot never carries a copy of either.
//
// Its box is computed in VIEWPORT px (getBoundingClientRect, clientWidth) but
// written as CSS px on an element inside <body> — and the desktop layout zooms
// <body> to 0.9 (`.gl-body`, _layout.scss), under which a fixed descendant's
// `left` / `top` render scaled by that zoom. So the computed px are divided by
// the body's effective zoom before they are written, or every portal bubble
// lands 10% short of its host — far from the origin, well off the hovered card
// (#13580 follow-up). The zoom is read off the measuring probe, which shares <body> — and
// therefore the effective zoom — with the bubble (`measureWithProbe` → `zoom`).
//
// Its coordinates are a snapshot of the host's viewport position, so a scroll
// or a window resize RE-PLACES it while the host is still shown (hovered, or
// keyboard focus-visible) and closes it otherwise. Re-place, not close, because
// of the reveal race (#12440): a pointer or a Tab arriving at a tile that
// straddles the strip's clipped edge first scrolls it into view, and the
// browser dispatches that scroll EVENT on the next frame — after the mouseover
// or focusin has already opened the portal. Closing on it would leave the
// still-hovered host to paint its own clipped CSS bubble, and whether the event
// lands before or after the show is a property of the machine, not the page.
// Every other host keeps
// the CSS bubble: the primitive stays complete without JS, and the portal is a
// fallback for geometry CSS cannot escape, not a second tooltip system.
//
// ARRIVAL — a tooltip that appears, changes or empties under a STILL pointer
// or focus (Group R, #12564). Every path above starts from an input event, and
// `closest('[data-gl-tooltip]')` on a mouseover finds no host in a button that
// has no tooltip yet. The order-sharing Add button gains its disabled-reason
// tooltip in the same React render that disables it, while the pointer that
// clicked it is still resting there: the CSS bubble shows from `:hover` alone,
// nothing judges its placement, and it can run off the 390px viewport until the
// pointer moves. Text that CHANGES under the pointer keeps the stamps of the old
// width the same way, and keyboard focus has the same gap. So the runtime also
// watches the one attribute document-wide: a MutationObserver filtered to
// `data-gl-tooltip` on <html> (which survives Turbo's <body> swap). The browser
// queues a record for that attribute only; the callback spends an
// `isConnected` plus `:hover` / focus-visible check per record and does layout
// for a host that is actually shown — never a sweep of the document. A host the
// per-host watch (portal, open popup) already covers is left to it, so each
// change is judged once.

const TOOLTIP_ATTR = "data-gl-tooltip";
const ALIGN_ATTR = "data-gl-tooltip-align";
const FLIP_ATTR = "data-gl-tooltip-flip";
const POS_ATTR = "data-gl-tooltip-pos";
const MULTILINE_ATTR = "data-gl-tooltip-multiline";
const PORTAL_ATTR = "data-gl-tooltip-portal";
// An OVERLAY disclosure trigger declares its open popup with BOTH of these:
// aria-haspopup names the popup it owns (dialog, menu, listbox), aria-expanded
// says it is open. The literal aria-haspopup="false" declares no popup. While
// a meaningful popup value and expanded state both hold, CSS hides the
// pseudo-bubble (_tooltip.scss, the same popup-state rule) and this runtime
// refuses the portal — because a bottom-placed bubble and the popup share the
// space below the button (R-30, PE-052 d, #12564). aria-expanded ALONE is not
// enough: an in-flow disclosure (a `.collapse` caret, a row-expand toggle)
// flips it too, its content never overlaps the bubble, and its tooltip is often
// the icon-only caret's only sighted label.
const EXPANDED_ATTR = "aria-expanded";
const POPUP_ATTR = "aria-haspopup";
const BUBBLE_ATTR = "data-gl-tooltip-bubble";
const BUBBLE_OPEN_ATTR = "data-gl-tooltip-bubble-open";
const BUBBLE_POS_ATTR = "data-gl-tooltip-bubble-pos";
// Where along the bubble's host-facing edge the arrow sits, in px from the
// bubble's start edge — the arrow stays centered on the HOST when the bubble
// hugs an edge, exactly as the CSS bubble's ::before does.
const ARROW_OFFSET_VAR = "--gl-tooltip-arrow-offset";
const SIDE_PLACEMENTS = new Set(["left", "right"]);
const OPPOSITE = { top: "bottom", bottom: "top", left: "right", right: "left" };
const CLIPPING_OVERFLOW = /hidden|clip|auto|scroll/;
// Keep the bubble this far inside the bounds — flush against a clipping edge
// reads as clipped even when technically visible.
const EDGE_MARGIN = 4;
// Host edge → bubble edge, in px. MUST equal `$gl-tooltip-gap` in
// _tooltip.scss — the flip decides whether a placement fits from it, so a
// drift here silently mis-places bubbles; spec/stylesheets/tooltip_edge_align_spec
// pins the two together.
export const BUBBLE_GAP = 9;

/**
 * Pure horizontal alignment decision, exported for unit tests.
 * @returns {"start"|"end"|null} the alignment to stamp, or null to keep centering
 */
export function edgeAlignFor({ hostLeft, hostRight, bubbleWidth, bounds, margin = EDGE_MARGIN }) {
  const center = (hostLeft + hostRight) / 2;
  const bubbleLeft = center - bubbleWidth / 2;
  const bubbleRight = center + bubbleWidth / 2;
  if (bubbleLeft < bounds.left + margin) return "start";
  if (bubbleRight > bounds.right - margin) return "end";
  return null;
}

/**
 * Pure flip decision, exported for unit tests. The authored placement stands
 * whenever it fits; otherwise the bubble flips to the opposite side when — and
 * only when — that side has MORE room. When neither side fits, the roomier one
 * wins (less of the bubble is lost), which may still be the authored side.
 * @returns {"top"|"bottom"|"left"|"right"|null} the flipped placement, or null
 */
export function flipFor({ hostRect, bubble, pos, bounds, margin = EDGE_MARGIN, gap = BUBBLE_GAP }) {
  const opposite = OPPOSITE[pos];
  if (!opposite) return null;
  const room = {
    top: hostRect.top - bounds.top,
    bottom: bounds.bottom - hostRect.bottom,
    left: hostRect.left - bounds.left,
    right: bounds.right - hostRect.right,
  };
  const required = (SIDE_PLACEMENTS.has(pos) ? bubble.width : bubble.height) + gap + margin;
  if (room[pos] >= required) return null;
  return room[opposite] > room[pos] ? opposite : null;
}

/**
 * Pure portal decision, exported for unit tests: true when NO CSS placement
 * can show the bubble whole inside the host's clip bounds — neither the
 * authored side nor its opposite has room along the placement axis, or the
 * bubble is wider (side placements: taller) than the bounds themselves, so an
 * edge hug cannot save it either. `flipFor` and `edgeAlignFor` answer "which
 * side / which hug"; this answers "is there any side at all".
 * @returns {boolean}
 */
export function needsPortal({ hostRect, bubble, pos, bounds, margin = EDGE_MARGIN, gap = BUBBLE_GAP }) {
  const placement = OPPOSITE[pos] ? pos : "top";
  const side = SIDE_PLACEMENTS.has(placement);
  const room = {
    top: hostRect.top - bounds.top,
    bottom: bounds.bottom - hostRect.bottom,
    left: hostRect.left - bounds.left,
    right: bounds.right - hostRect.right,
  };
  const required = (side ? bubble.width : bubble.height) + gap + margin;
  const axisFits = room[placement] >= required || room[OPPOSITE[placement]] >= required;
  const crossFits = side
    ? bubble.height + 2 * margin <= bounds.bottom - bounds.top
    : bubble.width + 2 * margin <= bounds.right - bounds.left;
  return !(axisFits && crossFits);
}

/**
 * Pure portal geometry, exported for unit tests: the fixed-position box for a
 * body-level bubble beside `hostRect`, judged against `bounds` (the viewport)
 * with the same flip and edge-hug rules as the CSS bubble, plus where the
 * arrow sits along the bubble's host-facing edge.
 * @returns {{ placement: "top"|"bottom"|"left"|"right", left: number, top: number, arrow: number }}
 */
export function portalPosition({ hostRect, bubble, pos, bounds, margin = EDGE_MARGIN, gap = BUBBLE_GAP }) {
  const authored = OPPOSITE[pos] ? pos : "top";
  const placement = flipFor({ hostRect, bubble, pos: authored, bounds, margin, gap }) || authored;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));
  const centerX = (hostRect.left + hostRect.right) / 2;
  const centerY = (hostRect.top + hostRect.bottom) / 2;

  if (SIDE_PLACEMENTS.has(placement)) {
    // `flipFor` keeps the authored side when neither fits and the opposite has
    // no MORE room, so beside a host on a narrow viewport the bubble can run
    // off either edge; clamp it in like the above/below branch does. The arrow
    // rides the vertical edge, so it needs no re-derivation from `left`.
    const preferred = placement === "left" ? hostRect.left - gap - bubble.width : hostRect.right + gap;
    const left = clamp(preferred, bounds.left + margin, bounds.right - margin - bubble.width);
    const top = clamp(centerY - bubble.height / 2, bounds.top + margin, bounds.bottom - margin - bubble.height);
    return { placement, left, top, arrow: centerY - top };
  }

  const top = placement === "top" ? hostRect.top - gap - bubble.height : hostRect.bottom + gap;
  const align = edgeAlignFor({ hostLeft: hostRect.left, hostRight: hostRect.right, bubbleWidth: bubble.width, bounds, margin });
  const preferred = align === "start" ? hostRect.left : align === "end" ? hostRect.right - bubble.width : centerX - bubble.width / 2;
  const left = clamp(preferred, bounds.left + margin, bounds.right - margin - bubble.width);
  return { placement, left, top, arrow: centerX - left };
}

// The visible viewport. `documentElement.clientWidth/Height` exclude the
// scrollbars; `window.innerWidth/Height` include them, so a bubble hugged to
// that edge would sit under the scrollbar. jsdom reports 0 for the client
// size, hence the fallback.
function viewportBounds() {
  const root = document.documentElement;
  return {
    left: 0,
    top: 0,
    right: root.clientWidth || window.innerWidth,
    bottom: root.clientHeight || window.innerHeight,
  };
}

// The rect the bubble must stay within: the viewport, narrowed by every
// ancestor that clips overflow on either axis. A `position: fixed` host is
// not clipped by its ancestors' overflow (until a transformed/filtered
// ancestor becomes its containing block), and nothing above a fixed ancestor
// clips what sits inside it.
function clipBounds(host) {
  const bounds = viewportBounds();
  let escaped = window.getComputedStyle(host).position === "fixed";
  for (let el = host.parentElement; el && el !== document.body; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    if (escaped) {
      if (style.transform === "none" && style.filter === "none") continue;
      escaped = false;
    }
    if (CLIPPING_OVERFLOW.test(`${style.overflowX} ${style.overflowY}`)) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        bounds.left = Math.max(bounds.left, rect.left);
        bounds.top = Math.max(bounds.top, rect.top);
        bounds.right = Math.min(bounds.right, rect.right);
        bounds.bottom = Math.min(bounds.bottom, rect.bottom);
      }
    }
    if (style.position === "fixed") escaped = true;
  }
  return bounds;
}

let probe = null;

/**
 * Default measurer: an offscreen element with the bubble's typography, padding
 * and width rules (`[data-gl-tooltip-measure]` in _tooltip.scss — an attribute,
 * like the primitive itself, so it stays outside the gl-* class registry), so
 * the box we compare against is the box the bubble will actually render at.
 * Exported for unit tests.
 * @returns {{ width: number, height: number }}
 */
export function measureWithProbe(text, { multiline = false } = {}) {
  if (!probe) {
    probe = document.createElement("span");
    probe.setAttribute("data-gl-tooltip-measure", "");
    probe.setAttribute("aria-hidden", "true");
  }
  // Turbo Drive renders each visit by replacing <body>, which detaches the
  // probe; a detached node measures 0, so every bubble would read as "fits"
  // and the flip would silently stop after the first navigation. Re-attach
  // whenever the probe is no longer in the live document.
  if (!probe.isConnected) document.body.appendChild(probe);
  // A multiline host wraps at its own line breaks — measure with the same rule.
  probe.toggleAttribute(MULTILINE_ATTR, multiline);
  probe.textContent = text;
  const rect = probe.getBoundingClientRect();
  return { width: rect.width, height: rect.height, zoom: effectiveZoom(probe, rect) };
}

// The effective CSS zoom of `el` — the factor a `position: fixed` descendant
// of a zoomed ancestor has its `left` / `top` px multiplied by when it paints.
// `currentCSSZoom` (Chrome 128+, Safari 17.4+, Firefox 126+) is the browser's
// own answer. Without it, the ZOOMED rect width over the UNZOOMED layout width
// (offsetWidth ignores zoom) gives the same factor; offsetWidth is rounded to
// whole px, so a ratio within half a percent of 1 is read as unzoomed rather
// than as a sub-pixel zoom. No layout (jsdom, a detached probe) → 1.
const ZOOM_NOISE = 0.005;
function effectiveZoom(el, rect) {
  const reported = el.currentCSSZoom;
  if (typeof reported === "number" && reported > 0) return reported;
  const layoutWidth = el.offsetWidth;
  if (!layoutWidth || !rect.width) return 1;
  const ratio = rect.width / layoutWidth;
  return Math.abs(ratio - 1) < ZOOM_NOISE ? 1 : ratio;
}

function stamp(host, attribute, value) {
  if (value) host.setAttribute(attribute, value);
  else host.removeAttribute(attribute);
}

// The one test both halves of the rule share — keep it in step with the
// `[data-gl-tooltip][aria-haspopup]:not([aria-haspopup='false'])[aria-expanded='true']` selector.
function popupOpen(host) {
  return host.hasAttribute(POPUP_ATTR) &&
    host.getAttribute(POPUP_ATTR) !== "false" &&
    host.getAttribute(EXPANDED_ATTR) === "true";
}

// "Shown" for the CSS primitive: the pointer is over the host, or the host is
// keyboard focus-visible. The gate is the browser's own `:focus-visible`, so a
// pointer-clicked host (focused, not focus-visible) never reads as shown.
function focusShown(host) {
  if (!host || !host.isConnected || document.activeElement !== host) return false;
  try {
    return host.matches(":focus-visible");
  } catch {
    return false;
  }
}
function shown(host) {
  return host.isConnected && (host.matches(":hover") || focusShown(host));
}

// The ONE body-level bubble every portaled host shares. Created on first use;
// re-attached after Turbo Drive swaps <body> (a detached bubble never paints).
let bubble = null;
let portalHost = null;

function ensureBubble() {
  if (!bubble) {
    bubble = document.createElement("div");
    bubble.setAttribute(BUBBLE_ATTR, "");
    // The bubble repeats what the host already exposes (aria-label or its
    // visible text plus data-gl-tooltip); announcing it too would double up.
    bubble.setAttribute("aria-hidden", "true");
  }
  if (!bubble.isConnected) document.body.appendChild(bubble);
  return bubble;
}

// While the portal shows for a host — and while a shown host's popup is open —
// the host's own attributes are watched: React re-renders a trigger with
// aria-expanded="true" (or drops its tooltip text) UNDER a pointer that has not
// moved, so no mouse or focus event would ever re-judge it — the bubble stayed
// painted over the popup it just opened until the pointer left. The observer
// re-judges on the attribute change itself; MutationObserver callbacks are
// microtasks, so the close lands before the next paint.
//
// The watch OUTLIVES the close an expansion causes, so the way back is
// symmetric with CSS: a CSS bubble returns the instant aria-expanded flips
// false because `:hover` re-evaluates with no event, and a portal host gets the
// same — clicking the trigger again, Close, Cancel and Done all leave the
// pointer resting on the button, and without the watch nothing would re-judge
// it until the pointer moved (review on the R-30 PR). The callback re-judges
// the host while it is still shown and lets the watch go once it is not.
//
// One observer per host: `showPortal` runs on every reposition (each capture-
// phase scroll and resize while a portal is open), so re-watching there would
// rebuild the observer per scroll frame for the same element and filter.
let hostObserver = null;
let watchedHost = null;
function unwatchHost() {
  if (hostObserver) hostObserver.disconnect();
  hostObserver = null;
  watchedHost = null;
}
function watchHost(host, measure) {
  if (hostObserver && watchedHost === host) return;
  unwatchHost();
  if (typeof MutationObserver !== "function") return;
  watchedHost = host;
  hostObserver = new MutationObserver(() => {
    // A watched host React has since REMOVED (an unmount under the pointer)
    // fires no mouseout or focusout; the only signal left is the node itself.
    // Release it — never re-place a bubble for a node outside the document.
    if (!host.isConnected) {
      if (host === portalHost) hidePortal();
      else unwatchHost();
      return;
    }
    if (host === portalHost || shown(host)) placeHost(host, measure);
    else unwatchHost();
  });
  hostObserver.observe(host, { attributes: true, attributeFilter: [TOOLTIP_ATTR, POPUP_ATTR, EXPANDED_ATTR] });
}
// `keepWatching`: the portal closes because the host's popup opened, and the
// host is still shown — keep re-judging it so the bubble returns on collapse.
function hidePortal({ keepWatching = false } = {}) {
  if (!keepWatching) unwatchHost();
  if (bubble) bubble.removeAttribute(BUBBLE_OPEN_ATTR);
  if (portalHost) portalHost.removeAttribute(PORTAL_ATTR);
  portalHost = null;
}

// Turbo Drive snapshots <body> (a deep clone) on `turbo:before-cache` and
// swaps that clone back in on a restore visit. Left attached, the bubble and
// the probe would ride along: each restore would add another
// `[data-gl-tooltip-bubble]` beside the one this module re-attaches, and a
// bubble open at cache time would come back open at stale coordinates. Close
// and detach both — `ensureBubble` / `measureWithProbe` re-attach on next use.
function detachForCache() {
  hidePortal();
  if (bubble) bubble.remove();
  if (probe) probe.remove();
}

function showPortal(host, text, rect, size, authored, measure) {
  const el = ensureBubble();
  const { placement, left, top, arrow } = portalPosition({ hostRect: rect, bubble: size, pos: authored, bounds: viewportBounds() });

  // Viewport px → the bubble's own CSS px. The bubble is a <body> child, so it
  // renders under the body's zoom (0.9 on desktop, see the header); the probe
  // that measured `size` is one too, so its zoom is the bubble's.
  const zoom = size.zoom > 0 ? size.zoom : 1;

  el.textContent = text;
  el.toggleAttribute(MULTILINE_ATTR, host.hasAttribute(MULTILINE_ATTR));
  el.setAttribute(BUBBLE_POS_ATTR, placement);
  el.style.left = `${left / zoom}px`;
  el.style.top = `${top / zoom}px`;
  el.style.setProperty(ARROW_OFFSET_VAR, `${arrow / zoom}px`);
  el.setAttribute(BUBBLE_OPEN_ATTR, "");

  // The host's own pseudo-bubble stays hidden while the portal shows, and the
  // edge stamps are meaningless for a bubble that is not the host's own.
  host.removeAttribute(ALIGN_ATTR);
  host.removeAttribute(FLIP_ATTR);
  host.setAttribute(PORTAL_ATTR, "");
  portalHost = host;
  watchHost(host, measure);
}

function placeHost(host, measure) {
  const text = host.getAttribute(TOOLTIP_ATTR);
  // No text — or an OPEN popup (`popupOpen`: aria-haspopup + aria-expanded):
  // nothing to show, so the same tidy-up runs for both.
  if (!text || popupOpen(host)) {
    // A dynamically-cleared tooltip has no bubble to place; drop any stamps
    // from when it still had text so they cannot outlive the text — and if
    // this host owns the portal bubble, close it too, or it would sit open
    // with the old text until some other host happened to show (review on
    // #12091: a re-render clears the text while the pointer is still over the
    // host, and the next mouseover across a descendant lands here). A host
    // with text whose popup is open stays watched (or starts being watched),
    // so its bubble comes back the moment the popup closes under the pointer.
    // A watched host that has LOST its text can show nothing again, so its
    // watch is released too — symmetric with the portal branch, which lets go
    // through hidePortal for the same reason.
    if (host === portalHost) hidePortal({ keepWatching: Boolean(text) });
    else if (text) watchHost(host, measure);
    else if (host === watchedHost) unwatchHost();
    host.removeAttribute(ALIGN_ATTR);
    host.removeAttribute(FLIP_ATTR);
    host.removeAttribute(PORTAL_ATTR);
    return;
  }

  const authored = host.getAttribute(POS_ATTR) || "top";
  const rect = host.getBoundingClientRect();
  const size = measure(text, { multiline: host.hasAttribute(MULTILINE_ATTR) });
  const bounds = clipBounds(host);

  if (needsPortal({ hostRect: rect, bubble: size, pos: authored, bounds })) {
    showPortal(host, text, rect, size, authored, measure);
    return;
  }
  // The same host re-judged under the pointer (a re-render or reflow gave its
  // clip bounds room) hands the portal back: close it, or the shared bubble
  // would stay open beside the CSS bubble the stamps below now paint. The
  // portal stamp tracks `portalHost` exactly (set in showPortal, stripped in
  // hidePortal), so hidePortal is the one place that removes it.
  if (host === portalHost) hidePortal();

  const flip = flipFor({ hostRect: rect, bubble: size, pos: authored, bounds });
  stamp(host, FLIP_ATTR, flip);

  // Side placements already sit beside the host; start/end alignment is an
  // above/below concern only.
  if (SIDE_PLACEMENTS.has(flip || authored)) {
    host.removeAttribute(ALIGN_ATTR);
    return;
  }
  stamp(host, ALIGN_ATTR, edgeAlignFor({
    hostLeft: rect.left,
    hostRight: rect.right,
    bubbleWidth: size.width,
    bounds,
  }));
}

let installed = false;

/**
 * Install the document-level listeners once.
 * @param {{ measure?: (text: string, options: { multiline: boolean }) => { width: number, height: number, zoom?: number } }} options
 *   — `measure` overrides the offscreen-probe measurer (tests; jsdom has no layout).
 *   `zoom` is the effective CSS zoom of the body the portal bubble renders in
 *   (its viewport-px box is divided by it before it is written); omitted = 1.
 */
export function initTooltipEdgeAlign({ measure = measureWithProbe } = {}) {
  if (installed) return;
  installed = true;

  const hostOf = (target) => (target instanceof Element ? target.closest(`[${TOOLTIP_ATTR}]`) : null);

  // The host the KEYBOARD opened a bubble for. A bubble shows while the pointer
  // is over its host OR the host is focus-visible, and there is one portal
  // bubble; so a pointer crossing another host borrows it, and when that host
  // closes the focused host gets it back (review on #12091: without this, a
  // stray mouse move silently killed a keyboard user's tooltip for good — the
  // host's own CSS bubble is the clipped one). `focusShown` gates on the
  // browser's `:focus-visible`, so a pointer-clicked host (focused, not
  // focus-visible) is never handed a bubble the pointer is nowhere near.
  let focusHost = null;
  const restoreFocusHost = () => {
    if (focusShown(focusHost)) placeHost(focusHost, measure);
    else focusHost = null;
  };

  const handle = (event) => {
    // A watched host that left the document with no attribute change (a React
    // unmount fires no leave event) is released on the next show anywhere,
    // so the watch never outlives its element by more than one pointer move.
    if (watchedHost && !watchedHost.isConnected) unwatchHost();
    const host = hostOf(event.target);
    if (event.type === "focusin") focusHost = host;
    // One bubble: whatever host shows next takes it over.
    if (host) {
      if (portalHost && host !== portalHost) hidePortal();
      placeHost(host, measure);
      return;
    }
    // Landed outside every host — the common mouseover, since most of the page
    // is not a host. With no portal open there is nothing to do; a bubble the
    // keyboard holds stays exactly as it is. Either way, re-placing the focused
    // host here would be a measure + ancestor walk per pointer move for the
    // same answer (review + audit on #12091). Only a portal some OTHER host
    // was borrowing closes, and focus, if it still holds a host, gets it back.
    if (!portalHost) return;
    if (portalHost === focusHost && focusShown(focusHost)) return;
    hidePortal();
    restoreFocusHost();
  };
  // Leaving a host (to anything outside it) ends that host's show; a mouseout
  // between the host's own children does not. Blur ends the keyboard show; a
  // pointer leaving a host that focus still holds keeps its bubble, and a
  // pointer leaving any other host hands the bubble back to focus.
  const leave = (event) => {
    const host = hostOf(event.target);
    if (!host) return;
    const next = event.relatedTarget;
    if (next instanceof Element && host.contains(next)) return;
    if (event.type === "focusout") {
      if (host === focusHost) focusHost = null;
      if (host === portalHost) hidePortal();
      else if (host === watchedHost && !shown(host)) unwatchHost();
      return;
    }
    if (host === portalHost) {
      if (host === focusHost && focusShown(host)) return;
      hidePortal();
    } else if (host === watchedHost && !shown(host)) {
      // An expanded host the pointer has left: nothing to bring back.
      unwatchHost();
    }
    restoreFocusHost();
  };

  // The portal's coordinates are a snapshot of the host's viewport position;
  // any scroll (the strip, the panel, the page) or resize invalidates them. A
  // host that is still shown keeps its bubble, re-placed where the host now
  // sits — that is also what survives the reveal race (#12440, header) — and
  // re-judged, so a scroll that gave the CSS bubble room hands the portal back.
  // A host no longer shown closes, and focus, if it still holds a host, gets
  // the bubble back.
  const reposition = () => {
    if (!portalHost) return;
    if (shown(portalHost)) {
      placeHost(portalHost, measure);
      return;
    }
    hidePortal();
    restoreFocusHost();
  };

  document.addEventListener("mouseover", handle, true);
  document.addEventListener("focusin", handle, true);
  document.addEventListener("mouseout", leave, true);
  document.addEventListener("focusout", leave, true);
  document.addEventListener("scroll", reposition, { capture: true, passive: true });
  window.addEventListener("resize", reposition, { passive: true });
  // Keyboard users dismiss a tooltip with Escape (WAI-ARIA tooltip pattern);
  // the CSS bubble can only be dismissed by moving focus, but the portal has
  // a real element to close. Capture phase so a component stopping the key
  // inside its own handler cannot leave the bubble behind.
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    hidePortal();
    // Dismissed is dismissed: a later pointer leave must not bring it back.
    // The next focusin (tab away and back) shows it again.
    focusHost = null;
  }, true);
  document.addEventListener("turbo:before-cache", () => {
    detachForCache();
    focusHost = null;
  });

  // A tooltip that ARRIVES, changes or empties under a still pointer or focus
  // (header, ARRIVAL). One record per changed host; a host the per-host watch
  // covers is skipped (that watch re-judges it), a host that is not shown costs
  // no layout at all, and a shown one is judged exactly as a mouseover would —
  // taking the one bubble over from another host, and becoming the focus host
  // when the keyboard holds it, so the usual hand-offs apply afterwards.
  if (typeof MutationObserver !== "function") return;
  const arrivals = new MutationObserver((records) => {
    const judged = new Set();
    for (const { target } of records) {
      if (!(target instanceof Element) || judged.has(target)) continue;
      judged.add(target);
      if (target === watchedHost || !shown(target)) continue;
      if (portalHost && target !== portalHost) hidePortal();
      if (focusShown(target)) focusHost = target;
      placeHost(target, measure);
    }
  });
  arrivals.observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: [TOOLTIP_ATTR] });
}
